import { Component, ElementRef, EventEmitter, OnInit, Input, Output, ViewChild } from '@angular/core';

// ionic
import { ModalController, PopoverController } from '@ionic/angular';
import { Events } from '../../helpers/events';

// rxjs
import { Subject } from "rxjs";
import { debounceTime, take, takeUntil } from "rxjs/operators";

// components
import { AddNewsfeedComponent } from "../add-newsfeed/add-newsfeed.component";
import { NewsfeedActionMenu } from "../newsfeed-action-menu/newsfeed-action-menu.component";

// other libraries
// import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { TranslateService } from "@ngx-translate/core";

// providers
import { EventService } from "../../services/event.service";
import { NewsfeedService } from "../../services/newsfeed.service";
import { OverlayService } from "../../services/overlay.service";
import { PlatformService } from "../../services/platform.service";
import { UserService } from "../../services/user.service";
import { ParticipantService } from "../../services/participant.service";
import { RouterExtService } from "../../services/router-ext.service";

// models
import { Event } from "../../models/event";
import { Newsfeed } from "../../models/newsfeed";
import { NewsfeedComment } from "../../models/newsfeed-comment";
import { NewsfeedLike } from "../../models/newsfeed-like";
import { User } from "../../models/user";
import { Group } from "../../models/group";

import { environment } from "../../../environments/environment";
import { Router } from '@angular/router';
import { isVideoLink } from 'src/app/services/helper';
import { isExternalLink } from 'src/app/helpers/url';

@Component({
    selector: 'newsfeed-detail',
    templateUrl: './newsfeed-detail.component.html',
    styleUrls: ['./newsfeed-detail.component.scss'],
})
export class NewsfeedDetailComponent implements OnInit {

    /**
     * actual event
     *
     * @type Event
     */
    @Input() event: Event;

    /**
     * selected newsfeed
     *
     * @type Newsfeed
     */
    @Input() newsfeed: Newsfeed;

    /**
     * selected newsfeed ID
     *
     * @type {number}
     */
    @Input() newsfeed_id: number;

    /**
     * show form in modal
     *
     * @type boolean
     */
    @Input() modal: boolean = false;
    @Input() showKeywords: boolean = true;

    @Output() onDelete: EventEmitter<Number> = new EventEmitter();

    /**
     * newsfeedBox
     *
     * @type ViewChild
     */
    @ViewChild('newsfeedBox') newsfeedBox: ElementRef;

    /**
     * Carousel slider
     *
     * @type {IonSlider}
     */
    @ViewChild('slider', { static: false }) slider;


    /**
     * Carousel slider
     *
     * @type {IonSlider}
     */
    @ViewChild('sliderBox') sliderBox: ElementRef;

    /**
     * newsfeed box
     *
     * @type {ElementRef}
     */
    @ViewChild('detail') detail: ElementRef;

    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * current event
     *
     * @type {User}
     */
    public user: User;

    /**
     * selected language
     *
     * @type string
     */
    public userLang: string;

    /**
     * initial loading state, we should have injected newsfeed model by default
     *
     * @type {boolean}
     */
    public loading: boolean = false;

    /**
     * validate state
     *
     * @type {boolean}
     */
    public validate: boolean = false;

    /**
     * Validation errors
     *
     * @type {object}
     */
    public validationErrors: object = {};

    /**
     * validation errors state
     *
     * @type {boolean}
     */
    public hasValidationErrors: boolean = false;

    /**
     * show edit mode
     *
     * @type {boolean}
     */
    public edit: boolean = false;

    public submit: boolean = false;
    public submitLike: boolean = false;

    postMaxLength = 250;

    public group: Group;

    public slideOpts = {
        initialSlide: 0,
        // speed: 400,
        pager: false,
        allowTouchMove: false
    };

    public currentCarouselIndex: number = 0;

    /**
     * env configuration
     */
    public env = environment;

    constructor(
        private router: Router,
        public appEvents: Events,
        public userService: UserService,
        public eventService: EventService,
        public translate: TranslateService,
        public newsfeedService: NewsfeedService,
        public plt: PlatformService,
        public overlayService: OverlayService,
        public modalController: ModalController,
        public popoverController: PopoverController,
        public routerExtService: RouterExtService,
        public participantService: ParticipantService
    ) {
        this.userLang = this.translate.currentLang;
    }

    ngOnInit() {
        // subscribe to current user
        this.userService.getCurrentUser().pipe(
            takeUntil(this.ngUnsubscribe),
            debounceTime(0)
        ).subscribe((user) => {
            if (user.id) {
                this.user = user;
            } else {
                this.user = null;
            }

            // get attendees group from event
            if (this.event.use_groups && this.event.groups.length && this.newsfeed && this.newsfeed.participant_id) {
                this.group = this.event.groups.find(item => item.id == this.newsfeed.participant.group_id);
            }

            // if newsfeed is not injected
            if (!this.newsfeed) {
                // load it from service
                this.loading = true
                this.newsfeedService.getById(+this.newsfeed_id).subscribe(
                    (newsfeed: Newsfeed) => {
                        this.loading = false;
                        this.newsfeed = new Newsfeed(newsfeed);
                        // open comments
                        this.newsfeed.commentsVisible = true;

                    });
            }

            // set proper width for slider
            setTimeout(() => {
                if (this.sliderBox && this.newsfeedBox) {
                    this.sliderBox.nativeElement.style.width = (this.detail.nativeElement.clientWidth - 42) + 'px';
                }
            }, 0);

        });
    }

    public deleteNewsfeed(id: number) {
        this.loading = true;
        this.newsfeedService.delete(id).subscribe(
            (success) => {
                this.overlayService.showSuccess(success.message);
                this.loading = false;

                // close modal if used
                if (this.modal) {
                    this.modalController.dismiss({
                        deleteId: id,
                        action: 'close'
                    });
                } else {
                    this.onDelete.emit(id);
                }
            },
            (error) => {
                this.overlayService.showConnectionProblems(error);
                this.loading = false;
            }
        );
    }

    /**
     * delete post confirmation
     *
     * @param id
     *
     * @return boolean
     */
    public async deleteNewsfeedConfirmation(id: number) {
        this.overlayService.showConfirm(
            this.translate.instant('NEWSFEED_DELETE_CONFIRMATION'),
            this.translate.instant('NEWSFEED_DELETE_CONFIRMATION_HINT'),
            () => { this.deleteNewsfeed(id) },
            () => { },
            this.translate.instant('NEWSFEED_DELETE_CONFIRMATION_BTN')
        );

        return false;
    }

    /**
     * edit uncorfimed newsfeed
     *
     * @param Newsfeed
     *
     * @return boolean
     */
    public async editUnconfirmedModal(newsfeed: Newsfeed) {
        this.overlayService.showConfirm(
            this.translate.instant('NEWSFEED_EDIT_UNCONFIRMED_MODAL'),
            this.event.newsfeed_post_visibility == 'keywords-limit' ?
                this.translate.instant('NEWSFEED_EDIT_UNCONFIRMED_LIMITED_MODAL_HINT', { count: this.event.newsfeed_keywords_limit }) :
                this.translate.instant('NEWSFEED_EDIT_UNCONFIRMED_MODAL_HINT'),
            () => { this.editNewsfeed(newsfeed) },
            () => { },
            this.translate.instant('NEWSFEED_EDIT_UNCONFIRMED_MODAL_EDIT_BTN'),
            this.translate.instant('NEWSFEED_EDIT_UNCONFIRMED_MODAL_CANCEL_BTN')
        );

        return false;
    }

    /**
     * edit newsfeed
     *
     * @param Newsfeed
     *
     * @return void
     */
    public editNewsfeed(newsfeed: Newsfeed) {
        if (this.plt.sizeSm) {
            this.editNewsfeddModal(newsfeed)
        } else {
            // use output to emit edit
            this.edit = true
        }
    }

    /**
     * open modal with newsfeed add form
     * @return void
     */
    public async editNewsfeddModal(newsfeed: Newsfeed) {

        this.routerExtService.softNavigate();

        const modal = await this.modalController.create({
            component: AddNewsfeedComponent,
            cssClass: 'newsfeed-form',
            componentProps: {
                modal: true,
                displayBigTextarea: true,
                showKeywords: this.showKeywords,
                event: this.event,
                user: this.user,
                newsfeed: newsfeed
            }
        });

        modal.onWillDismiss().then((data) => {

            if (data.data?.newsfeed) {
                this.newsfeed = data.data.newsfeed;
            }

            if (data.data?.action == 'close' || data.role == 'backdrop') {
                this.routerExtService.softBack();
            }

        });

        return await modal.present();
    }

    public newsfeedSended(event?: Newsfeed) {
        this.newsfeed = event;
        this.edit = false;
    }

    /**
     * show newsfeed comments
     *
     * @return void
     */
    public showComments() {
        // dont display comment where there is no comment and posting comments is disabled
        if (!this.newsfeed.comments.length && !this.event.can(this.user.selected_participant.group_id, 'allow_newsfeed_comment')) {
            return false;
        }

        // on mobile we will use modal to show all details about comments
        if (!this.modal && this.plt.sizeSm) {
            this.showModalDetail();
        } else {
            this.newsfeed.commentsVisible = !this.newsfeed.commentsVisible
        }
    }

    /**
     * cancel comments edit
     *
     * @return void
     */
    public cancelCommentEdit() {
        this.newsfeed.editComment = new NewsfeedComment;
    }

    /**
     * save comments for newsfeed
     *
     * @return void
     */
    public postComment(newsfeed) {
        this.validationErrors = {};
        this.validate = true;
        this.submit = true;

        this.newsfeedService.postComment(newsfeed).subscribe(
            (success) => {
                this.overlayService.showSuccess(success.message);
                newsfeed.comments = success.comments.map(comment => new NewsfeedComment(comment));

                this.validate = false;
                this.submit = false;

                // show last comment if new one
                if (this.modal && !newsfeed.editComment.id) {
                    this.scrollPageToBottom();
                }
                //this.scrollPageToBottom();
                newsfeed.editComment = new NewsfeedComment;

                // publish changes in post to update details in list
                if (this.modal) {
                    this.appEvents.publish('newsfeed:post:udpated', this.newsfeed);
                }
            },
            (error) => {
                this.submit = false;
                const data = error.error;

                if (data.fields) {
                    for (const field in data.fields) {
                        if (data.fields.hasOwnProperty(field)) {
                            this.hasValidationErrors = true;
                            this.validationErrors[field] = data.fields[field].join(' ');
                        }
                    }
                }

                this.overlayService.showError(data.message);
            }
        );
    }

    /**
     * toggle like for newsfeed
     *
     * @param Newsfeed
     *
     * @return void
     */
    public toggleLike(newsfeed) {
        if (this.submitLike) {
            return;
        }

        this.validationErrors = {};
        this.validate = true;
        this.submitLike = true;

        this.newsfeedService.toggleLike(newsfeed).subscribe(
            (success) => {
                this.overlayService.showSuccess(success.message);
                // NOTE[jg] - this is used to check if current user liked this post...
                this.newsfeed.likes = success.likes.map(like => new NewsfeedLike(like));
                this.newsfeed.number_of_likes = success.number_of_likes
                this.validate = false;
                this.submitLike = false;

                // publish changes in post to update details in list
                if (this.modal) {
                    this.appEvents.publish('newsfeed:post:udpated', this.newsfeed);
                }
            },
            (error) => {
                this.overlayService.showError(error.message);
                this.submitLike = false;
            }
        );
    }

    /**
     * delete comment
     *
     * @param Newsfeed
     *
     * @return number
     */
    public isLiked(newsfeed) {
        return newsfeed.likes.filter((item) => {
            return item.participant_id == this.user.selected_participant_id;
        }).length;
    }

    /**
     * open edit for comment
     *
     * @param Newsfeed
     * @param NewsfeedComment
     *
     * @return void
     */
    public editComment(newsfeed, newsfeedComment) {
        this.validationErrors = [];
        this.hasValidationErrors = false;
        newsfeed.editComment = new NewsfeedComment(newsfeedComment);
    }

    /**
     * delete comment
     *
     * @param Newsfeed
     * @param id
     *
     * @return void
     */
    public deleteComment(newsfeed, id: number) {
        this.loading = true;
        this.newsfeedService.deleteComment(newsfeed, id).subscribe(
            (success) => {
                // cancel editing comment when it is deleted
                if (this.newsfeed.editComment && this.newsfeed.editComment.id == id) {
                    this.cancelCommentEdit();
                }

                this.overlayService.showSuccess(success.message);
                newsfeed.comments = newsfeed.comments.filter((item) => { return item.id != id });
                this.loading = false;

                // publish changes in post to update details in list
                if (this.modal) {
                    this.appEvents.publish('newsfeed:post:udpated', this.newsfeed);
                }
            },
            (error) => {
                this.overlayService.showConnectionProblems(error);
                this.loading = false;
            }
        );
    }

    /**
     * confirm for deleting comment
     *
     * @return void
     */
    public async deleteNewsfeedCommentConfirmation(newsfeed, id: number) {
        this.overlayService.showConfirm(
            this.translate.instant('NEWSFEED_DELETE_COMMENT_CONFIRMATION'),
            this.translate.instant('NEWSFEED_DELETE_COMMENT_CONFIRMATION_HINT'),
            () => { this.deleteComment(newsfeed, id) },
            () => { },
            this.translate.instant('NEWSFEED_DELETE_COMMENT_CONFIRMATION_BTN')
        );
    }

    /**
     * open newsfeed detail with comments in new modal
     *
     * @return modal
     */
    public async showModalDetail(shortText: boolean = false) {

        // open modal only if we see this newsfeed in list
        if (!this.modal && this.plt.sizeSm) {

            this.routerExtService.softNavigate();

            // open comments
            this.newsfeed.commentsVisible = true;

            const detail = await this.modalController.create({
                component: NewsfeedDetailComponent,
                cssClass: 'newsfeed-detail',
                componentProps: {
                    newsfeed: this.newsfeed,
                    event: this.event,
                    modal: true
                }
            });

            detail.onWillDismiss().then((data) => {
                if (data.data) {
                    if (data.data.keywords) {
                        // TODO[jg] refresh details about comments and so...
                    }
                    if (data.data?.action == 'close' || data.role == 'backdrop') {

                        let link = this.plt.defaultLink + this.routerExtService.currentUrl.split("?")[0].split("#")[0].replace('/tabs', '');
                        if (this.plt.tabMenu && !this.routerExtService.currentUrl.includes('/tabs')) {
                            this.router.navigateByUrl(link);
                        } else {
                            this.routerExtService.softNavigate(link);
                        }

                    } else {
                        // do nothing for back navigation from browser..
                        // just close modal and update history
                        this.routerExtService.popHistory();
                    }

                    // delete event from opened modal
                    if (data.data.deleteId) {
                        this.onDelete.emit(data.data.deleteId);
                    }
                }
            });

            return await detail.present();
        }

        if (!this.plt.sizeSm && shortText) {
            this.newsfeed.longTextVisible = true
        }
    }

    /**
     * go to participants detail page by newsfeed
     *
     * @param newsfeed
     *
     * @return void
     */
    public async showAttendeeDetail(newsfeed: Newsfeed) {
        if (newsfeed.getRealParticipant()) {
            if (this.modal) {
                this.modalController.dismiss();
            }
            this.appEvents.publish('attendee:show', newsfeed.getRealParticipant().id);
        }
    }

    /**
     * go to participants detail page by newsfeed comment
     *
     * @param comment
     *
     * @return void
     */
    public async showAttendeeDetailByComment(comment: NewsfeedComment) {
        if (comment.getRealParticipant()) {
            if (this.modal) {
                this.modalController.dismiss();
            }
            this.appEvents.publish('attendee:show', comment.getRealParticipant().id);
        }
    }

    /**
     * close modal window
     *
     * @return void
     */
    public backClicked() {
        this.modalController.dismiss({ action: 'close' });
    }

    /**
     * close modal window
     *
     */
    public newsfeedEditationCanceled(event?: Newsfeed) {
        this.newsfeed = event;
        this.edit = false;
    }

    openLink(url: string, external: boolean = true) {
        if (isVideoLink(url)) {
            this.appEvents.publish('video:show', url);
        } else {
            if (external) {

                if (this.plt.is('cordova')) {
                    // cordova is not working with third options attribute
                    window.open(url, '_system');
                } else {
                    window.open(url, '_system', 'noreferrer');
                }

            } else {
                // check by domain if external or not...
                if (!isExternalLink(url)) {
                    // app internal links
                    let urlPath = new URL(url);
                    this.router.navigateByUrl(this.plt.defaultLink + urlPath.pathname.replace(this.plt.defaultLink, '') + urlPath.search);
                    // close modal if used, avoid sending close action, so now have empty data
                    // otherwise there would be some redirect
                    if (this.modal) {
                        this.modalController.dismiss();
                    }
                } else {
                    // redirect to specific page
                    window.location.href = url;
                }
            }
        }
    }

    /**
     * scroll chat to bottom
     *
     * @return void
     */
    public scrollPageToBottom(): void {
        try {
            setTimeout(() => {
                // scroll whole page to bottom, used for keyboard to focus textarea
                document.querySelector('.modal').scrollTo(0, document.querySelector('.modal').scrollHeight);
                // NOTE[jg] this is now working.. ion-item is not element, but ionic component...
                //this.newsfeedBox.nativeElement.scrollTo(0, this.newsfeedBox.nativeElement.scrollHeight);
            }, 150);
        } catch (err) {
            return;
        }
    }

    /**
     * show popover with action menu
     * @return void
     */
    async showActionMenu(ev: any, newsfeed: Newsfeed) {
        const popover = await this.popoverController.create({
            component: NewsfeedActionMenu,
            event: ev,
            componentProps: {
                newsfeed: newsfeed
            }
        });

        popover.onWillDismiss().then((data) => {
            if (data && data.data) {
                if (data.data.action == 'mute' && data.data.item) {
                    this.loading = true;
                    this.user.selected_participant.blacklist.newsfeed_participants.push(data.data.item.participant_id);

                    this.participantService.updateBlacklist(this.user.selected_participant.blacklist).subscribe(
                        (success) => {
                            // update newsfeed list
                            this.appEvents.publish('newsfeed:refresh');
                            this.loading = false;
                        },
                        (error) => {
                            this.overlayService.showConnectionProblems(error);
                            this.loading = false;
                        }
                    );
                } else if (data.data.action == 'unmute' && data.data.item) {
                    this.loading = true;
                    this.user.selected_participant.blacklist.newsfeed_participants = this.user.selected_participant.blacklist.newsfeed_participants.filter(item => item != data.data.item.participant_id);

                    this.participantService.updateBlacklist(this.user.selected_participant.blacklist).subscribe(
                        (success) => {
                            // update newsfeed list
                            this.appEvents.publish('newsfeed:refresh');
                            this.loading = false;
                        },
                        (error) => {
                            this.overlayService.showConnectionProblems(error);
                            this.loading = false;
                        }
                    );
                }
            }
        });

        await popover.present();
    }

    slidePrev() {
        this.slider.slidePrev();
    }

    slideNext() {
        this.slider.slideNext();
    }

    /**
     * sync step attribute with slider
     *
     * @return void
     */
    setStep() {
        this.slider.getActiveIndex().then((index) => {
            this.currentCarouselIndex = index;
        });
    }
}
